import { useCallback, useEffect, useMemo } from "react";
import { useAppContext } from "../../contexts/AppContext";
import { buildEntitlementSelectors } from "./selectors";
import { logEntitlementsUsage } from "./entitlementsUsageDebug";
import { logEntitlementsRuntimeAudit } from "./entitlementsRuntimeAudit";
import { canUseEntitlementFeatureKey } from "./entitlementFeatureKeyMap";
import { isAdminUser, getUserDetails } from "../../services/authService";
import {
  applyDevPackageTierToEntitlementsPayload,
  useDevViewModeOptional,
} from "../../dev/viewMode";

/**
 * Agent commercial entitlements from POST /api/agents/me/entitlements.
 * למשתמש admin ב-localStorage: במצבי idle/loading מנוחים boolean עד תשובת השרת (תואם effective PRO).
 * עם DEV package override — מטריצת חבילה לתצוגה מקדימה (ללא שינוי מנוי אמיתי).
 */
export function useAgentEntitlements() {
  const {
    agentEntitlements,
    agentEntitlementsStatus,
    agentEntitlementsError,
    agentEntitlementsLoading,
    refreshAgentEntitlements,
  } = useAppContext();

  const devView = useDevViewModeOptional();
  const packageTierOverride =
    devView?.isPackageOverrideActive && devView.packageTier
      ? devView.packageTier
      : null;

  const effectivePayload = useMemo(
    () =>
      applyDevPackageTierToEntitlementsPayload(
        agentEntitlements,
        packageTierOverride,
      ),
    [agentEntitlements, packageTierOverride],
  );

  const adminEffectivePro =
    isAdminUser() && !packageTierOverride && !devView?.isPackageOverrideActive;

  const selectors = useMemo(
    () =>
      buildEntitlementSelectors(effectivePayload, {
        status: agentEntitlementsStatus,
        adminEffectivePro,
      }),
    [effectivePayload, agentEntitlementsStatus, adminEffectivePro],
  );

  const canUseFeature = useCallback(
    (featureKey) => {
      const v = canUseEntitlementFeatureKey(selectors, featureKey);
      if (v === null && import.meta.env.DEV) {
        console.warn("[ENTITLEMENTS_UNKNOWN_FEATURE_KEY]", { featureKey });
      }
      return v === null ? false : v;
    },
    [selectors]
  );

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const u = getUserDetails();
    const role = String(u?.role ?? "").trim().toLowerCase();
    const blocking_can_flags = Object.entries(selectors)
      .filter(([k, v]) => k.startsWith("can") && v === false)
      .map(([k]) => k);
    console.log("[ADMIN_ENTITLEMENTS_TRACE]", {
      role,
      is_admin: Boolean(u?.is_admin ?? u?.isAdmin),
      package_id: u?.package_id ?? u?.packageId ?? null,
      package_code: u?.package_code ?? u?.packageCode ?? u?.plan ?? null,
      effectivePackage: selectors.effectivePackageCode ?? null,
      devPackageTierOverride: packageTierOverride,
      entitlementsStatus: agentEntitlementsStatus,
      blocking_can_flags: blocking_can_flags.slice(0, 32),
    });
  }, [agentEntitlementsStatus, selectors, packageTierOverride]);

  useEffect(() => {
    if (agentEntitlementsStatus === "ok" && effectivePayload) {
      logEntitlementsRuntimeAudit(effectivePayload, selectors);
    }
    logEntitlementsUsage({
      status: agentEntitlementsStatus,
      error: agentEntitlementsError,
      package: effectivePayload?.package ?? null,
      featureKeys:
        effectivePayload?.features &&
        typeof effectivePayload.features === "object"
          ? Object.keys(effectivePayload.features)
          : [],
      canSearchHotels: selectors.canSearchHotels,
      canSearchPackages: selectors.canSearchPackages,
      canSearchFlights: selectors.canSearchFlights,
      canUseDynamicPackages: selectors.canUseDynamicPackages,
      canOrganizedTours: selectors.canOrganizedTours,
      canUseLeadsSystem: selectors.canUseLeadsSystem,
      canUseAiSales: selectors.canUseAiSales,
      hasPrioritySupport: selectors.hasPrioritySupport,
      commissionPercentage: selectors.commissionPercentage,
      hotelHrCommissionPercentage: selectors.hotelHrCommissionPercentage,
      effectiveCommissionPercentage: selectors.effectiveCommissionPercentage,
      maxMonthlyOrders: selectors.maxMonthlyOrders,
      canUseMonthlyPricing: selectors.canUseMonthlyPricing,
      canUseCustomerOfferLink: selectors.canUseCustomerOfferLink,
      canUseMultiPackageCustomerOffer: selectors.canUseMultiPackageCustomerOffer,
    });
  }, [
    effectivePayload,
    agentEntitlementsStatus,
    agentEntitlementsError,
    selectors.canSearchHotels,
    selectors.canSearchPackages,
    selectors.canSearchFlights,
    selectors.canUseDynamicPackages,
    selectors.canOrganizedTours,
    selectors.canUseLeadsSystem,
    selectors.canUseAiSales,
    selectors.hasPrioritySupport,
    selectors.commissionPercentage,
    selectors.hotelHrCommissionPercentage,
    selectors.effectiveCommissionPercentage,
    selectors.maxMonthlyOrders,
    selectors.canUseMonthlyPricing,
    selectors.canUseCustomerOfferLink,
    selectors.canUseMultiPackageCustomerOffer,
  ]);

  return {
    data: effectivePayload,
    status: agentEntitlementsStatus,
    error: agentEntitlementsError,
    loading: agentEntitlementsLoading,
    refresh: refreshAgentEntitlements,
    canUseFeature,
    ...selectors,
  };
}
